import React, { useState, useEffect, useRef } from "react";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";

import { ReactComponent as SeatSection1Svg } from "../../../../assets/recital2026/seatSection1.svg";
import { ReactComponent as SeatSection2Svg } from "../../../../assets/recital2026/seatSection2.svg";
import "../../../../styles/scss/recital2026Seat.scss";

function SeatPage() {
  useLoginCheck();
  const [resultSeats, setResultSeats] = useState([]);
  const [selectedSeat, setSelectedSeat] = useState(null);

  useEffect(() => {
    const fetchResultSeats = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}/api/get_recital2026_result_seats/`
        );
        if (!response.ok) {
          throw new Error("ネットワークレスポンスが異常です。");
        }
        const data = await response.json();
        setResultSeats(data);
        console.log(data);
      } catch (error) {
        console.error("データの取得中にエラーが発生しました:", error);
      }
    };

    fetchResultSeats();
  }, []);

  const [info, setInfo] = useState({
    seatNo: "-",
    studioName: "-",
    name: "-",
  });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isSvgHovered, setIsSvgHovered] = useState(false);

  // SVGの座席にdata属性を設定し、色を付ける
  useEffect(() => {
    if (resultSeats.length === 0) return;

    // 初期表示時に全ての座席にdata属性を設定
    const rects = document.querySelectorAll("rect[data-seat_id]");
    rects.forEach((rect) => {
      const seatId = rect.getAttribute("data-seat_id");

      // 既存の色をリセット
      rect.style.fill = "";
      rect.removeAttribute("data-studioname");
      rect.removeAttribute("data-name");

      // 座席が割り当てられているか確認
      const assignedSeat = resultSeats.find(
        (seat) => seat.results && seat.results.includes(seatId)
      );

      if (assignedSeat) {
        // 割り当て済みの座席にdata属性を設定
        rect.setAttribute("data-studioname", assignedSeat.studio || "-");
        rect.setAttribute("data-name", assignedSeat.name || "-");
        // 初期表示時、確定している席をグレーにする
        rect.style.fill = "#cccccc";
      }
    });
  }, [resultSeats]);

  // 選択中の顧客の座席に色を付ける
  useEffect(() => {
    if (!selectedSeat) return;

    const rects = document.querySelectorAll("rect[data-seat_id]");
    rects.forEach((rect) => {
      const seatId = rect.getAttribute("data-seat_id");

      // 選択中の顧客の座席かどうか確認
      if (selectedSeat.results && selectedSeat.results.includes(seatId)) {
        rect.style.fill = "#ffd700"; // 選択中の顧客の座席を金色に
      } else {
        // 他の顧客の座席かどうか確認
        const assignedSeat = resultSeats.find(
          (seat) =>
            seat.id !== selectedSeat.id &&
            seat.results &&
            seat.results.includes(seatId)
        );

        if (assignedSeat) {
          rect.style.fill = "#cccccc"; // 他の顧客の座席をグレーに
        } else {
          rect.style.fill = ""; // 未割り当ての座席は元の色に
        }
      }
    });
  }, [selectedSeat, resultSeats]);

  useEffect(() => {
    // マウスの移動を追跡
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.pageX, y: e.pageY }); // 修正点:clientX/YからpageX/Yに変更
    };
    window.addEventListener("mousemove", handleMouseMove);

    // SVGのパス要素にイベントリスナーを追加
    const paths = document.querySelectorAll("rect");
    const handlePathMouseOver = (e) => {
      const path = e.currentTarget;
      const seatNo = path.getAttribute("data-seat_id") || "-";
      const studioName = path.getAttribute("data-studioname") || "-";
      const name = path.getAttribute("data-name") || "-";
      setInfo({ seatNo, studioName, name });
    };
    const handlePathMouseOut = () => {
      setInfo({
        seatNo: "-",
        studioName: "-",
        name: "-",
      });
    };
    paths.forEach((path) => {
      path.addEventListener("mouseover", handlePathMouseOver);
      path.addEventListener("mouseout", handlePathMouseOut);
    });

    // SVGのクリックイベント
    const handleSeatClick = async (e) => {
      if (!selectedSeat) return;

      const rect = e.currentTarget; // 先に要素を保存
      const seatId = rect.getAttribute("data-seat_id");
      if (!seatId) return;

      // 他の顧客が確定している席はクリックできない
      const otherAssignedSeat = resultSeats.find(
        (seat) =>
          seat.id !== selectedSeat.id &&
          seat.results &&
          seat.results.includes(seatId)
      );

      if (otherAssignedSeat) {
        alert("この座席は既に他の方が確定しています。");
        return;
      }

      // 現在の座席リストを取得
      let currentResults = selectedSeat.results
        ? [...selectedSeat.results]
        : [];

      // 座席が既に選択されているか確認
      const seatIndex = currentResults.indexOf(seatId);

      if (seatIndex > -1) {
        // 既に選択されている場合は削除
        currentResults.splice(seatIndex, 1);
      } else {
        // 新しく座席を追加する前にバリデーション
        // data-seat_idから部と座席ランクを取得（例: "s1-1-12-48" -> 1部, Sランク）
        const match = seatId.match(/^s(\d+)-/);
        if (!match) return;
        const section = match[1]; // 部（1 or 2）

        // 座席のクラスからランクを取得
        let rank = "";
        if (rect.classList.contains("seat-s")) {
          rank = "s";
        } else if (rect.classList.contains("seat-a")) {
          rank = "a";
        } else if (rect.classList.contains("seat-b")) {
          rank = "b";
        }

        // 申込内容のフィールド名を決定
        const fieldName = `${section === "1" ? "first" : "second"}_${rank}`;
        const appliedCount = selectedSeat[fieldName] || 0;

        // 申し込んでいない座席の場合
        if (appliedCount === 0) {
          alert(
            `この座席種類（${section}部指定席${rank.toUpperCase()}）は申し込まれていません。`
          );
          return;
        }

        // 既に選択されている同じ種類の座席数をカウント
        const selectedCountOfSameType = currentResults.filter((id) => {
          const idMatch = id.match(/^s(\d+)-/);
          if (!idMatch) return false;
          const idSection = idMatch[1];

          // 同じ部かチェック
          if (idSection !== section) return false;

          // 同じランクかチェック
          const existingRect = document.querySelector(
            `rect[data-seat_id="${id}"]`
          );
          if (!existingRect) return false;

          if (rank === "s" && existingRect.classList.contains("seat-s"))
            return true;
          if (rank === "a" && existingRect.classList.contains("seat-a"))
            return true;
          if (rank === "b" && existingRect.classList.contains("seat-b"))
            return true;

          return false;
        }).length;

        // 申込数を超える場合
        if (selectedCountOfSameType >= appliedCount) {
          alert(
            `${section}部指定席${rank.toUpperCase()}の申込数（${appliedCount}席）を超えています。`
          );
          return;
        }

        // 新しく座席を追加
        currentResults.push(seatId);
      }

      // APIで更新
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}/api/update_recital2026_result_seat/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: selectedSeat.id,
              results: currentResults,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("座席の更新に失敗しました。");
        }

        // ローカルの状態を更新
        const updatedResultSeats = resultSeats.map((seat) =>
          seat.id === selectedSeat.id
            ? { ...seat, results: currentResults }
            : seat
        );
        setResultSeats(updatedResultSeats);
        setSelectedSeat({ ...selectedSeat, results: currentResults });

        // data属性を更新（非同期処理の前に保存したrectを使用）
        if (currentResults.includes(seatId)) {
          rect.setAttribute("data-studioname", selectedSeat.studio || "-");
          rect.setAttribute("data-name", selectedSeat.name || "-");
        } else {
          rect.removeAttribute("data-studioname");
          rect.removeAttribute("data-name");
        }
      } catch (error) {
        console.error("座席の更新中にエラーが発生しました:", error);
        alert("座席の更新に失敗しました。");
      }
    };

    paths.forEach((path) => {
      path.addEventListener("click", handleSeatClick);
    });

    // すべてのSVG要素にイベントリスナーを追加
    const svgElements = document.querySelectorAll("svg");
    const handleSvgMouseOver = () => {
      setIsSvgHovered(true);
    };
    const handleSvgMouseOut = () => {
      setIsSvgHovered(false);
    };
    svgElements.forEach((svgElement) => {
      svgElement.addEventListener("mouseover", handleSvgMouseOver);
      svgElement.addEventListener("mouseout", handleSvgMouseOut);
    });

    // マウスストーカーのターゲット要素にイベントリスナーを追加
    const targets = document.querySelectorAll(".stkr-target");
    const handleTargetMouseOver = () => {
      setScale(2);
    };
    const handleTargetMouseOut = () => {
      setScale(1);
    };
    targets.forEach((target) => {
      target.addEventListener("mouseover", handleTargetMouseOver);
      target.addEventListener("mouseout", handleTargetMouseOut);
    });

    // クリーンアップ
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      paths.forEach((path) => {
        path.removeEventListener("mouseover", handlePathMouseOver);
        path.removeEventListener("mouseout", handlePathMouseOut);
        path.removeEventListener("click", handleSeatClick);
      });
      svgElements.forEach((svgElement) => {
        svgElement.removeEventListener("mouseover", handleSvgMouseOver);
        svgElement.removeEventListener("mouseout", handleSvgMouseOut);
      });
      targets.forEach((target) => {
        target.removeEventListener("mouseover", handleTargetMouseOver);
        target.removeEventListener("mouseout", handleTargetMouseOut);
      });
    };
  }, [selectedSeat, resultSeats]);

  const offsetX = 10; // マウスカーソルの右に10px
  const offsetY = -10; // マウスカーソルの上に10px

  // 顧客行をクリックした時の処理
  const handleRowClick = (seat) => {
    setSelectedSeat(seat);
  };

  return (
    <>
      <div
        id="stkr"
        style={{
          position: "absolute",
          left: mousePosition.x + offsetX,
          top: mousePosition.y + offsetY,
          transform: `scale(${scale})`,
          visibility: isSvgHovered ? "visible" : "hidden",
          pointerEvents: "none",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          padding: "10px",
          borderRadius: "5px",
        }}
      >
        <span>席番 </span>
        {info.seatNo}
        <br />
        <span>スタジオ </span>
        {info.studioName}
        <br />
        <span>名前 </span>
        {info.name}
      </div>

      <div className="section">
        {/* 1部 */}
        <h2>1部</h2>
        {/* 左カラム */}
        <div className="seat">
          <div className="DIV_IMG">
            <SeatSection1Svg />
          </div>
        </div>

        <h2>2部</h2>
        {/* 左カラム */}
        <div className="seat">
          <div className="DIV_IMG">
            <SeatSection2Svg />
          </div>
        </div>
      </div>
      {/* 右カラム */}
      <div className="seatlistArea">
        <div className="seatList">
          <ul className="seatlist_section">
            <li className="header">部</li>
            <li className="header">名前</li>
            <li className="header">スタジオ</li>
            <li className="header">1S</li>
            <li className="header">1A</li>
            <li className="header">1B</li>
            <li className="header">2S</li>
            <li className="header">2A</li>
            <li className="header">2B</li>
            <li className="header">座席</li>
            {/*  */}
            {resultSeats.map((seat, index) => (
              <React.Fragment key={index}>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.section}
                </li>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.name}
                </li>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.studio}
                </li>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.first_s}
                </li>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.first_a}
                </li>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.first_b}
                </li>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.second_s}
                </li>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.second_a}
                </li>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.second_b}
                </li>
                <li
                  onClick={() => handleRowClick(seat)}
                  style={{
                    backgroundColor:
                      selectedSeat?.id === seat.id ? "#ffeb3b" : "",
                    cursor: "pointer",
                  }}
                >
                  {seat.results
                    ? seat.results
                        .map((s) => {
                          // s の形式は "s1-1-1-18"
                          // 各部(部-階-列-番)に分割する
                          const match = s.match(/^s(\d+)-(\d+)-(\d+)-(\d+)$/);
                          if (!match) return s;
                          const [, section, floor, row, num] = match;
                          return `${section}部${floor}階${row}列${num}番`;
                        })
                        .join(" / ")
                    : ""}
                </li>
              </React.Fragment>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

export default SeatPage;
